import * as d3 from 'd3';
import 'intersection-observer';
import scrollama from 'scrollama';

const main = d3.select('body');
const story = main.select('#story');
const $graphic = story.select('.story--graphic');
const article = story.select('.story--article');
const step = article.selectAll('.story--article__step');

// Graphic config
const ippdConfig = window.ippd || {};
const topNavHeight = ippdConfig.topNavHeight || 0;
let HEIGHT = 0;
let WIDTH = 0;
const MARGIN = { top: 30, bottom: 40, left: 50, right: 25 };
const EASE = d3.easeCubicInOut;
const SEC = 1000;
const DURATION = SEC * 3;
let minR = 4;
let maxR = 16;

const colorScale = {
  ไทย: '#4281A1',
  เกาหลีใต้: '#D5514A',
  มาเลเซีย: '#d5aa2a',
  เวียดนาม: '#5D7E0F',
  อินโดนีเซีย: '#5cc156',
  แอฟริกา: '#faa109',
  อเมริกา: '#e77dae',
  'เอเชีย & โอเชียเนีย': '#c45ed2',
  ยุโรป: '#706dc8',
  country5: '#64b5d0',
};

// Graphic selection
const $svg = story.select('svg#vis');
const $gVis = $svg.select('.g-vis');
const $gAxis = $svg.select('.g-axis');
const $gTooltip = $svg.select('.g-tooltips');
const $world = $gVis.select('.world');
const $figcap = $graphic.select('figcaption');

// Data
let rawData = null;
let surveyData = null;
let dict = null;
let indexNames = null;
let indexSorts = null;
let indexUnits = null;

// Graphic State
let countries = null;
let trend = null;
let start = null;
let current = null;
let end = null;
let yi = null;
let xi = null;

let currentStep = 'intro';

let scroller = scrollama();

function pickData({ country, year, [yi]: y, [xi]: x }) {
  return { country, year: +year, y: +y, x: +x };
}

// Helper Function
function getScaleX(data) {
  // scales
  return d3
    .scaleLinear()
    .domain(d3.extent(data, d => d.x))
    .nice()
    .range([0, WIDTH]);
}

function getScaleY(data) {
  return d3
    .scaleLinear()
    .domain(d3.extent(data, d => d.y))
    .nice()
    .range([HEIGHT, 0]);
}

function getDuration({ leave, reverse }) {
  let factor = 1;
  if (leave) factor = 0;
  else if (reverse) factor = 0.33;
  const slow = DURATION * factor;
  const medium = Math.floor(slow * 0.33);
  const fast = Math.floor(slow * 0.1);
  return {
    slow,
    medium,
    fast,
  };
}

function getLine({ scaleX, scaleY }) {
  return d3
    .line()
    .x(d => scaleX(d.x))
    .y(d => scaleY(d.y))
    .curve(d3.curveMonotoneX)
    .defined(d => d.y);
}

// Graphic Update Function
function updateAxis({ scaleX, scaleY, dur, ease = EASE }) {
  const axisY = d3.axisLeft(scaleY);

  const $gAxisY = $gAxis.select('.axis--y');
  $gAxisY
    .transition()
    .duration(dur.slow)
    .ease(ease)
    .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`)
    .call(axisY);

  const gridY = d3
    .axisRight(scaleY)
    .tickSize(WIDTH)
    .tickFormat('');
  const $gGridY = $gAxis.select('.grid--y').attr('opacity', 0.1);
  $gGridY
    .transition()
    .duration(dur.slow)
    .ease(ease)
    .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`)
    .call(gridY);

  const $gLabelY = $gAxis.select('.label--y');
  $gLabelY
    .selectAll('.y-label')
    .data([yi])
    .join(
      enter =>
        enter
          .append('text')
          .attr('class', 'y-label')
          .attr('font-size', 12.5)
          .attr('text-anchor', 'start')
          .attr('dominant-baseline', 'central'),
      // .attr('transform', 'rotate(-90)'),
      update => update,
      exit => exit.remove()
    )
    .attr('y', MARGIN.top / 2)
    .attr('x', MARGIN.left * 1.05)
    .text(d => indexNames[d]);

  const axisX = d3
    .axisBottom(scaleX)
    .tickFormat(xi === 'year' ? d3.format('d') : d3.format(',d'));
  const $gAxisX = $gAxis.select('.axis--x');
  $gAxisX
    .transition()
    .duration(dur.slow)
    .ease(ease)
    .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top + HEIGHT})`)
    .call(axisX);

  const gridX = d3
    .axisTop(scaleX)
    .tickSize(HEIGHT)
    .tickFormat('');
  const $gGridX = $gAxis.select('.grid--x').attr('opacity', 0.1);
  $gGridX
    .transition()
    .duration(dur.slow)
    .ease(ease)
    .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top + HEIGHT})`)
    .call(gridX);

  const $gLabelX = $gAxis.select('.label--x');
  $gLabelX
    .selectAll('.x-label')
    .data([xi])
    .join(
      enter =>
        enter
          .append('text')
          .attr('class', 'x-label')
          .attr('font-size', 12.5)
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'central'),
      update => update,
      exit => exit.remove()
    )
    .attr('x', MARGIN.left + WIDTH)
    .attr('y', HEIGHT + MARGIN.top + MARGIN.bottom * 0.75)
    .text(d => indexNames[d]);
}

function updateDimensions() {
  // will be called when resize happens
  const figcapHeight = $figcap.node().offsetHeight;
  HEIGHT =
    window.innerHeight -
    topNavHeight -
    figcapHeight -
    MARGIN.top -
    MARGIN.bottom;
  WIDTH = $graphic.node().offsetWidth - MARGIN.left - MARGIN.right;
  minR = WIDTH / 125 > 4 ? 4 : WIDTH / 125;
}

function enterCircles($country, { scaleX, scaleY, r = minR }) {
  const $c = $country
    .select('.circles')
    .selectAll('circle')
    .data(d => {
      return d.values;
    });

  const $enter = $c
    .enter()
    .append('circle')
    .attr('cx', d => scaleX(d.x))
    .attr('cy', d => scaleY(d.y))
    .attr('r', r)
    .attr('stroke', d => colorScale[d.country])
    .attr('fill', 'white');
  $enter
    .merge($c)
    .attr('data-x', d => scaleX(d.x))
    .attr('data-y', d => scaleY(d.y))
    .attr('stroke-dasharray', 0); // for making circle not affected by animated-dotted class

  $c.exit().remove();
}

function updatePath($country, { scaleX, scaleY, render = true, dur = 0 }) {
  const line = getLine({ scaleX, scaleY });
  if ($country.datum().values.length > 1) {
    $country.selectAll('path').data(d => [d.values]);
    if (render)
      $country
        .selectAll('path')
        .attr('d', line)
        .attr('stroke', d => colorScale[d[0].country])
        .attr('stroke-dasharray', null)
        .attr('stroke-dashoffset', null);
  }
}

function enterCountry($country) {
  $country.attr('data-id', d => d.key);
  $country.append('path').attr('class', 'line');
  $country.append('g').attr('class', 'circles');
}

function exitCountry($country, dur) {
  // EXIT
  $country
    .exit()
    .transition()
    .duration(dur)
    .style('opacity', 0)
    .remove();
}

function enterTooltip($country, { scaleX, scaleY, tooltipData, year = false }) {
  $country.attr('fill', d => `url('#${d.country}')`).attr('r', maxR);

  $gTooltip.selectAll('*').remove();
  const $tooltip = $gTooltip
    .selectAll('g.tooltip-world')
    .data(tooltipData)
    .enter()
    .append('g')
    .attr('class', 'tooltip-world');
  const $tooltipEnter = $tooltip
    .selectAll('g.tooltip-country')
    .data(d => d)
    .enter()
    .append('g')
    .attr('class', 'tooltip-country');

  $tooltipEnter
    .append('line')
    .attr('class', 'x-intercept')
    .attr('x1', d => scaleX(d.x))
    .attr('y1', scaleY.range()[0])
    .attr('x2', d => scaleX(d.x))
    .attr('y2', d => scaleY(d.y))
    .attr('stroke', d => colorScale[d.country]);

  $tooltipEnter
    .append('text')
    .attr('class', 'x-annotate')
    .attr('x', d => scaleX(d.x))
    .attr('y', d => scaleY(d.y))
    .attr('dx', 3.75)
    .attr('dy', 25.5)
    .attr('fill', d => colorScale[d.country])
    .attr('text-anchor', 'start')
    .attr('dominant-baseline', 'central')
    .text(d => (xi === 'year' ? d3.format('d') : d3.format(',d'))(d.x));

  $tooltipEnter
    .append('line')
    .attr('class', 'y-intercept')
    .attr('x1', scaleX.range()[0])
    .attr('y1', d => scaleY(d.y))
    .attr('x2', d => scaleX(d.x))
    .attr('y2', d => scaleY(d.y))
    .attr('stroke', d => colorScale[d.country]);

  $tooltipEnter
    .append('text')
    .attr('class', 'y-annotate')
    .attr('x', d => scaleX(d.x))
    .attr('y', d => scaleY(d.y))
    .attr('dx', -23.75)
    .attr('dy', -7.5)
    .attr('fill', d => colorScale[d.country])
    .attr('text-anchor', 'end')
    .attr('dominant-baseline', 'central')
    .text(d => d3.format(',.2f')(d.y));

  if (year) {
    $tooltipEnter
      .append('text')
      .attr('class', 'year-annotate')
      .attr('x', d => scaleX(d.x))
      .attr('y', d => scaleY(d.y))
      .attr('dx', 3.75)
      .attr('dy', -25.5)
      .attr('fill', 'black')
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'central')
      .text(d => 'ปี ' + d3.format('d')(d.year));
  }
}

function exitTooltip() {
  $gTooltip.selectAll('*').remove();
}

function enterDottedLine(countries) {
  $world
    .selectAll('g.country')
    .classed('animate-dotted', g => countries.includes(g.key))
    .classed('low-opacity', g => !countries.includes(g.key));

  const $paths = $world.selectAll('path');
}

function exitDottedLine() {
  $world
    .selectAll('g.country')
    .classed('animate-dotted', false)
    .classed('low-opacity', false);
}

function graphicOff() {
  $graphic
    .transition()
    .duration(1000)
    .style('opacity', 0)
    .transition();
}
function graphicOn() {
  $graphic
    .transition()
    .duration(1000)
    .style('opacity', 1);
}

function enterYearAnno($country, { scaleX, scaleY, dur }) {
  const $yearAnno = $country;
  $yearAnno
    .append('g')
    .attr('class', 'year-anno')
    .selectAll('.anno-text')
    .data(d => d.values)
    .join(enter => enter.append('text').attr('class', 'anno-text'))
    .attr('x', d => scaleX(d.x))
    .attr('y', d => scaleY(d.y))
    .attr('dx', 5)
    .attr('dy', -5)
    .attr('stroke', 'white')
    .attr('paint-order', 'stroke')
    .attr('stroke-width', 2)
    .text(d => d.year)
    .attr('opacity', 0)
    .transition()
    .duration(dur.medium)
    .delay(dur.medium + dur.slow)
    .attr('opacity', 1);
}

function exitYearAnno() {
  $gVis.select('.year-anno').remove();
}

function resetAnimationOnReverse(reverse) {
  if (reverse) {
    $svg.selectAll('*').interrupt();
  }
}

const STEP = {
  intro: ({ reverse, leave }) => {
    graphicOff();

    // reverse !== false because on init() resize called this function with reverse = false and leave = true
    if (leave && reverse !== false) graphicOn();
  },
  'thai-workhour': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP.intro({ leave: true });
    yi = 'work_hours_per_person';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(d => d.country === 'ไทย');
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    const $countryTooltip = $countrySet.filter(d =>
      [2000, 2005, 2015].includes(d.x)
    );
    const tooltipData = grouped.map(({ key, values }) =>
      values.filter(d => [2000, 2005, 2015].includes(d.year))
    );
    $countryTooltip.call(enterTooltip, { scaleX, scaleY, tooltipData });

    enterDottedLine(['ไทย']);
    // highlight
    const $hlAxisY = article.selectAll('.highlight-axis-y');
    $hlAxisY
      .on('mouseenter', function() {
        $gAxis
          .selectAll('.label--y')
          .transition()
          .duration(dur.fast)
          .attr('transform', 'scale(1.1)');
      })
      .on('mouseleave', function() {
        $gAxis
          .selectAll('.label--y')
          .transition()
          .duration(dur.fast)
          .attr('transform', 'scale(1)');
      });

    exitCountry($country, dur.fast);
  },
  'continent-workhour': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['thai-workhour']({ leave: true });
    yi = 'work_hours_per_person';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(d =>
      ['ไทย', 'แอฟริกา', 'อเมริกา', 'เอเชีย & โอเชียเนีย', 'ยุโรป'].includes(
        d.country
      )
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    const tooltipCondition = d => [2017].includes(d.year);

    const $countryTooltip = $countrySet.filter(tooltipCondition);
    const tooltipData = grouped.map(({ key, values }) =>
      values.filter(tooltipCondition)
    );
    $countryTooltip.call(enterTooltip, { scaleX, scaleY, tooltipData });
    enterDottedLine(['ไทย', 'ยุโรป']);

    exitCountry($country, dur.fast);
  },
  'neighbor-workhour-1': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['thai-workhour']({ leave: true });
    yi = 'work_hours_per_person';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d =>
        !['แอฟริกา', 'อเมริกา', 'เอเชีย & โอเชียเนีย', 'ยุโรป'].includes(
          d.country
        )
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    const tooltipCondition = d =>
      [2007, 2017].includes(d.year) && ['ไทย', 'เกาหลีใต้'].includes(d.country);

    const $countryTooltip = $countrySet.filter(tooltipCondition);
    const tooltipData = grouped.map(({ key, values }) =>
      values.filter(tooltipCondition)
    );
    $countryTooltip.call(enterTooltip, { scaleX, scaleY, tooltipData });
    enterDottedLine(['ไทย', 'เกาหลีใต้']);

    exitCountry($country, dur.fast);
  },
  'neighbor-workhour-2': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['neighbor-workhour-1']({ leave: true });
    yi = 'work_hours_per_person';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d =>
        !['แอฟริกา', 'อเมริกา', 'เอเชีย & โอเชียเนีย', 'ยุโรป'].includes(
          d.country
        )
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    const tooltipCondition = d =>
      ([2009, 2017].includes(d.year) &&
        ['ไทย', 'เวียดนาม'].includes(d.country)) ||
      ([2000, 2017].includes(d.year) && ['มาเลเซีย'].includes(d.country)) ||
      ([2017].includes(d.year) && ['อินโดนีเซีย'].includes(d.country));

    const $countryTooltip = $countrySet.filter(tooltipCondition);
    const tooltipData = grouped.map(({ key, values }) =>
      values.filter(tooltipCondition)
    );
    $countryTooltip.call(enterTooltip, { scaleX, scaleY, tooltipData });
    enterDottedLine(['ไทย', 'เวียดนาม', 'มาเลเซีย', 'อินโดนีเซีย']);

    exitCountry($country, dur.fast);

    graphicOn();
  },
  'gdp-workhour-intro': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['neighbor-workhour-2']({ leave: true });

    graphicOff();
    if (leave) {
      graphicOn();
    }
  },
  'gdp-workhour-1': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['gdp-workhour-intro']({ leave: true });
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d =>
        !['แอฟริกา', 'อเมริกา', 'เอเชีย & โอเชียเนีย', 'ยุโรป'].includes(
          d.country
        )
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    exitTooltip();
    exitDottedLine();
    exitCountry($country, dur.fast);
  },
  'gdp-workhour-2': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['gdp-workhour-1']({ leave: true });
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'year';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d =>
        !['แอฟริกา', 'อเมริกา', 'เอเชีย & โอเชียเนีย', 'ยุโรป'].includes(
          d.country
        )
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur: { slow: 0 } });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    $countryMerge.call(updatePath, { scaleX, scaleY });
    $countryMerge.call(enterCircles, { scaleX, scaleY });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.fast)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', 'white')
      .attr('r', minR);

    const tooltipCondition = d =>
      [2017].includes(d.year) &&
      ['ไทย', 'เกาหลีใต้', 'มาเลเซีย'].includes(d.country);

    const $countryTooltip = $countrySet.filter(tooltipCondition);
    const tooltipData = grouped.map(({ key, values }) =>
      values.filter(tooltipCondition)
    );
    $countryTooltip.call(enterTooltip, { scaleX, scaleY, tooltipData });
    enterDottedLine(['ไทย', 'เกาหลีใต้', 'มาเลเซีย']);
    exitYearAnno();
    exitCountry($country, dur.fast);
  },
  'connected-scatter-1': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['gdp-workhour-2']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(d => d.country === 'ไทย');
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur });

    const $country = $world.selectAll('g.country').data(grouped, d => d.key);
    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);
    // $countryMerge.call(updatePath, { scaleX, scaleY });

    const line = getLine({ scaleX, scaleY });
    $countryMerge.selectAll('path').data(d => [d.values]);
    let $countryPath = $countryMerge
      .selectAll('path')
      .attr('d', line)
      .attr('stroke', d => colorScale[d[0].country]);
    $countryPath.each((d, i, p) => {
      const pathLength = p[0].getTotalLength();
      d3.select(p[0])
        .attr('stroke-dasharray', `${pathLength}, ${pathLength}`)
        .attr('stroke-dashoffset', pathLength)
        .transition()
        .delay(dur.medium)
        .duration(dur.slow)
        .attr('stroke-dashoffset', leave ? null : 0);
    });
    $countryMerge.call(enterCircles, { scaleX, scaleY });
    $countryMerge.call(enterYearAnno, { scaleX, scaleY, dur });

    const $countrySet = $countryMerge
      .selectAll('circle')
      .transition()
      .duration(dur.medium)
      .ease(EASE)
      .attr('cx', d => scaleX(d.x))
      .attr('cy', d => scaleY(d.y))
      .attr('fill', (d, i, n) =>
        i === n.length - 1 ? `url('#${d.country}')` : 'white'
      )
      .attr('r', (d, i, n) => (i === n.length - 1 ? maxR : minR));

    if (leave) exitYearAnno();
    exitTooltip();
    exitDottedLine();
    exitCountry($country, dur.fast);
  },
  'connected-scatter-2': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['connected-scatter-1']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d => !['เกาหลีใต้', 'แอฟริกา', 'อเมริกา', 'ยุโรป'].includes(d.country)
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur });

    const existingCountries = $world
      .selectAll('g.country')
      .data()
      .map(d => d.key);
    const $country = $world.selectAll('g.country').data(grouped, d => d.key);

    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);

    if (!reverse && !leave) {
      const line = getLine({ scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('d', line)
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1);

      $countryMerge
        .selectAll('circle')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR))
        .on('end', function(d, i, n) {
          if (i === n.length - 1) showCountryJoin();
        });
    } else {
      showCountryJoin();
    }

    // exitTooltip();
    exitYearAnno();
    exitCountry($country, dur.fast);

    function showCountryJoin() {
      $countryMerge.call(updatePath, { scaleX, scaleY, render: true });
      $countryMerge.call(enterCircles, { scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .attr('opacity', d =>
          !existingCountries.includes(d[0].country) ? 0 : 1
        )
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1);

      const $countrySet = $countryMerge
        .selectAll('circle')
        .attr('opacity', d => (!existingCountries.includes(d.country) ? 0 : 1))
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR));

      if (!leave) {
        const tooltipCondition = d => [2017].includes(d.year);

        const $countryTooltip = $countrySet.filter(tooltipCondition);
        const tooltipData = grouped.map(({ key, values }) =>
          values.filter(tooltipCondition)
        );
        $countryTooltip.call(enterTooltip, {
          scaleX,
          scaleY,
          tooltipData,
          year: true,
        });
        enterDottedLine([
          'ไทย',
          'เวียดนาม',
          'มาเลเซีย',
          'อินโดนีเซีย',
          'เอเชีย & โอเชียเนีย',
        ]);
      }
    }

    if (leave) exitTooltip();
  },
  'connected-scatter-3': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['connected-scatter-2']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(
      d => !['แอฟริกา', 'อเมริกา', 'ยุโรป'].includes(d.country)
    );
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur });

    const existingCountries = $world
      .selectAll('g.country')
      .data()
      .map(d => d.key);
    const $country = $world.selectAll('g.country').data(grouped, d => d.key);

    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);

    let i = 0;
    if (!reverse && !leave) {
      const line = getLine({ scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('d', line)
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1);

      $countryMerge
        .selectAll('circle')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR))
        .on('end', function(d, i, n) {
          if (i === n.length - 1) showCountryJoin();
        });
    }
    if (leave & !reverse) {
      showCountryJoin();
    }

    // exitTooltip();
    exitYearAnno();
    exitCountry($country, dur.fast);

    function showCountryJoin() {
      $countryMerge.call(updatePath, { scaleX, scaleY, render: true });
      $countryMerge.call(enterCircles, { scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .attr('opacity', d =>
          !existingCountries.includes(d[0].country) ? 0 : 1
        )
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1);

      const $countrySet = $countryMerge
        .selectAll('circle')
        .attr('opacity', d => (!existingCountries.includes(d.country) ? 0 : 1))
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR));

      if (!leave) {
        const tooltipCondition = d =>
          [2000, 2017].includes(d.year) &&
          ['ไทย', 'เกาหลีใต้'].includes(d.country);

        const $countryTooltip = $countrySet.filter(tooltipCondition);
        const tooltipData = grouped.map(({ key, values }) =>
          values.filter(tooltipCondition)
        );
        $countryTooltip.call(enterTooltip, {
          scaleX,
          scaleY,
          tooltipData,
          year: true,
        });
        enterDottedLine(['ไทย', 'เกาหลีใต้']);
      }
    }

    if (leave) exitTooltip();
  },
  'connected-scatter-4': ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['connected-scatter-3']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur });

    const existingCountries = $world
      .selectAll('g.country')
      .data()
      .map(d => d.key);
    const $country = $world.selectAll('g.country').data(grouped, d => d.key);

    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);

    if (!reverse && !leave) {
      const line = getLine({ scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('d', line)
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1);

      $countryMerge
        .selectAll('circle')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR))
        .on('end', function(d, i, n) {
          if (i === n.length - 1) showCountryJoin();
        });
    } else {
      showCountryJoin();
    }

    if (leave) exitTooltip();
    exitYearAnno();
    exitCountry($country, dur.fast);

    function showCountryJoin() {
      $countryMerge.call(updatePath, { scaleX, scaleY, render: true });
      $countryMerge.call(enterCircles, { scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .attr('opacity', d =>
          !existingCountries.includes(d[0].country) ? 0 : 1
        )
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1);

      const $countrySet = $countryMerge
        .selectAll('circle')
        .attr('opacity', d => (!existingCountries.includes(d.country) ? 0 : 1))
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR));

      const tooltipCondition = d =>
        [2017].includes(d.year) &&
        ['ไทย', 'อเมริกา', 'ยุโรป'].includes(d.country);

      const $countryTooltip = $countrySet.filter(tooltipCondition);
      const tooltipData = grouped.map(({ key, values }) =>
        values.filter(tooltipCondition)
      );
      $countryTooltip.call(enterTooltip, {
        scaleX,
        scaleY,
        tooltipData,
        year: true,
      });
      enterDottedLine(['ไทย', 'อเมริกา', 'ยุโรป']);
    }
  },
  outro: ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['connected-scatter-4']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(d => d.country === 'ไทย');
    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    // exitCountry($country, dur);

    const scaleX = getScaleX(data);
    const scaleY = getScaleY(data);

    updateAxis({ scaleX, scaleY, dur });

    const existingCountries = $world
      .selectAll('g.country')
      .data()
      .map(d => d.key);
    const $country = $world.selectAll('g.country').data(grouped, d => d.key);

    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);

    if (!reverse && !leave) {
      const line = getLine({ scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('d', line)
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1);

      $countryMerge
        .selectAll('circle')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR))
        .on('end', function(d, i, n) {
          if (i === n.length - 1) showCountryJoin();
        });
    } else {
      showCountryJoin();
    }

    // exitTooltip();
    exitYearAnno();
    exitCountry($country, dur.fast);

    function showCountryJoin() {
      $countryMerge.call(updatePath, { scaleX, scaleY, render: true });
      $countryMerge.call(enterCircles, { scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .attr('opacity', d =>
          !existingCountries.includes(d[0].country) ? 0 : 1
        )
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1);

      const $countrySet = $countryMerge
        .selectAll('circle')
        .attr('opacity', d => (!existingCountries.includes(d.country) ? 0 : 1))
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR));

      const tooltipCondition = d =>
        [2017].includes(d.year) && ['ไทย'].includes(d.country);

      const $countryTooltip = $countrySet.filter(tooltipCondition);
      const tooltipData = grouped.map(({ key, values }) =>
        values.filter(tooltipCondition)
      );
      $countryTooltip.call(enterTooltip, {
        scaleX,
        scaleY,
        tooltipData,
        year: true,
      });
      enterDottedLine(['ไทย']);
    }

    if (leave) exitTooltip();
  },
  survey: ({ reverse, leave }) => {
    if (!reverse && !leave) STEP['outro']({ leave: true });
    resetAnimationOnReverse(reverse);
    yi = 'gdp_per_capita_per_working_hours';
    xi = 'work_hours_per_person';
    const dur = getDuration({ leave, reverse });
    let data = rawData.map(d => pickData(d));
    data = data.filter(d => d.country === 'ไทย');
    let survey = surveyData.reduce((prev, current) =>
      prev.ratio > current.ratio ? prev : current
    );
    survey = pickData(survey);
    data = [...data, survey];

    let grouped = d3
      .nest()
      .key(d => d.country)
      .entries(data);

    // exitCountry($country, dur);

    const scaleX = getScaleX(data);
    let [minX, maxX] = scaleX.domain();
    scaleX.domain([minX * 0.95, maxX]);

    const scaleY = getScaleY(data);
    let [minY, maxY] = scaleY.domain();
    scaleY.domain([minY, maxY * 1.05]);

    updateAxis({ scaleX, scaleY, dur });

    const existingCountries = $world
      .selectAll('g.country')
      .data()
      .map(d => d.key);
    const $country = $world.selectAll('g.country').data(grouped, d => d.key);

    const $countryEnter = $country
      .enter()
      .append('g')
      .attr('class', 'country')
      .call(enterCountry);

    const $countryMerge = $countryEnter.merge($country);

    if (!reverse && !leave) {
      const line = getLine({ scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('d', line)
        .attr('stroke-dashoffset', 0)
        .style('opacity', 1);

      $countryMerge
        .selectAll('circle')
        .transition()
        .duration(dur.slow)
        .ease(EASE)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR))
        .on('end', function(d, i, n) {
          if (i === n.length - 1) showCountryJoin();
        });
    } else {
      showCountryJoin();
    }

    // exitTooltip();
    exitYearAnno();
    exitCountry($country, dur.fast);

    function showCountryJoin() {
      $countryMerge.call(updatePath, { scaleX, scaleY, render: true });
      $countryMerge.call(enterCircles, { scaleX, scaleY });
      $countryMerge
        .selectAll('path')
        .attr('opacity', d =>
          !existingCountries.includes(d[0].country) ? 0 : 1
        )
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1);

      const $countrySet = $countryMerge
        .selectAll('circle')
        .attr('opacity', d => (!existingCountries.includes(d.country) ? 0 : 1))
        .transition()
        .duration(leave ? 0 : dur.medium)
        .ease(EASE)
        .attr('opacity', 1)
        .attr('cx', d => scaleX(d.x))
        .attr('cy', d => scaleY(d.y))
        .attr('fill', 'white')
        .attr('r', (d, i, n) => (i === n.length - 1 ? minR * 2 : minR));

      const tooltipCondition = d =>
        ([2018].includes(d.year) && ['country5'].includes(d.country)) ||
        ([2017].includes(d.year) && ['ไทย'].includes(d.country));

      const $countryTooltip = $countrySet.filter(tooltipCondition);
      const tooltipData = grouped.map(({ key, values }) =>
        values.filter(tooltipCondition)
      );
      $countryTooltip.call(enterTooltip, {
        scaleX,
        scaleY,
        tooltipData,
        year: true,
      });
      $countryTooltip
        .filter(d => d.country === 'country5')
        .attr('fill', d => colorScale[d.country]);
      enterDottedLine(['ไทย', 'country5']);
    }
  },
};

function setupScroller() {
  scroller
    .setup({
      step: '#story .story--article .story--article__step',
      offset: 0.9,
      // debug: true
    })
    .onStepEnter(handleStepEnter);
}

function resizeScroll() {
  scroller.resize();
}

async function resize() {
  updateDimensions();

  $svg
    .attr('height', MARGIN.top + HEIGHT + MARGIN.bottom)
    .attr('width', MARGIN.left + WIDTH + MARGIN.right);

  $gVis.attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

  resizeScroll();
  updateStep({ reverse: false, leave: true });
}

function updateStep({ reverse = true, leave = false }) {
  if (STEP[currentStep]) STEP[currentStep]({ reverse, leave });
}

function handleStepEnter({ element, index, direction }) {
  currentStep = d3.select(element).attr('data-step');
  updateStep({ reverse: direction === 'up' });

  step.classed('story--article__passed', function(d, i) {
    return i <= index;
  });
}

function init(
  loadedData,
  loadedDict,
  loadedIndexNames,
  loadedIndexSorts,
  loadedIndexUnits,
  loadedSurveyData
) {
  rawData = loadedData.filter(d => !Object.values(d).includes(NaN));
  surveyData = loadedSurveyData;
  indexNames = loadedIndexNames;
  indexUnits = loadedIndexUnits;

  resize();
  setupScroller();
}

export { init as storyInit, resize as storyResize };
